import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';
import ItemPresetInput from '../shared/ItemPresetInput';

const SumpPumpInspectionModule = ({ onBack, currentUser }) => {
  // Meta definition for all steps
  const steps = [
    { id: 1, key: 'homeowner', title: 'Homeowner Information' },
    { id: 2, key: 'arrival', title: 'Arrival & Initial Assessment' },
    { id: 3, key: 'electrical', title: 'Electrical & Power Safety' },
    { id: 4, key: 'pit', title: 'Sump Pit Cleanliness & Condition' },
    { id: 5, key: 'pump', title: 'Pump Condition Assessment' },
    { id: 6, key: 'float', title: 'Float Switch & Activation Test' },
    { id: 7, key: 'check_valve', title: 'Check Valve Inspection' },
    { id: 8, key: 'discharge', title: 'Discharge Line Inspection' },
    { id: 9, key: 'backup', title: 'Backup Pump System Test' },
    { id: 10, key: 'alarms', title: 'Alarm & Sensor Systems' },
    { id: 11, key: 'basement', title: 'Basement Moisture & Foundation Scan' },
    { id: 12, key: 'photos', title: 'Inspection Photos' },
    { id: 13, key: 'summary', title: 'Technician Summary Classification' },
    { id: 14, key: 'recommendations', title: 'Recommendations & Upgrades' },
    { id: 15, key: 'pdf', title: 'View & Share Web Report' },
  ];

  const defaultRecommendations = [
    {
      code: 'replace_primary_pump',
      label: 'Replace primary sump pump',
      guideline: '$500',
      selected: false,
      price: null,
    },
    {
      code: 'add_backup_pump',
      label: 'Add battery backup pump system',
      guideline: '$2,000',


      selected: false,
      price: null,


    },
    {
      code: 'install_high_water_alarm',
      label: 'Install high-water alarm',
      guideline: '$150',
      selected: false,
      price: null,
    },
    {
      code: 'replace_check_valve',
      label: 'Replace noisy / failing check valve',
      guideline: '$150',
      selected: false,
      price: null,
    },
    {
      code: 'improve_interior_abs',
      label: 'Improve interior ABS piping',
      guideline: '$200',
      selected: false,
      price: null,
    },
    {
      code: 'exterior_discharge_repair',
      label: 'Exterior discharge line repair',
      guideline: '$200',
      selected: false,
      price: null,
    },
    {
      code: 'dry_well_install_trench',
      label: 'Dry well installation & trench',
      guideline: '$1,500',
      selected: false,
      price: null,
    },
    {
      code: 'heat_trace_cable',
      label: 'Heat trace cable option',
      guideline: '$500',
      selected: false,
      price: null,
    },
    {
      code: 'other',
      label: 'Other (specify in notes)',
      guideline: '',
      selected: false,
      price: null,
      notes: '',
    },
  ];

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [openSteps, setOpenSteps] = useState([steps[0].key]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(false);
  const [hasSentReport, setHasSentReport] = useState(false);
  const [formData, setFormData] = useState({
    homeowner: {
      homeowner_name: '',
      address: '',
      city: '',
      province: '',
      postal_code: '',
      phone: '',
      email: '',
      inspection_date: new Date().toISOString().split('T')[0],
      technician_name: currentUser?.full_name || '',
    },
    arrival: {
      has_power: null,
      pump_running_constantly: null,
      water_in_pit: null,
      audible_alarm: null,
      visible_alarm: null,
      notes: '',
      photos: [],
    },
    electrical: {
      dedicated_circuit: null,
      gfci_protected: null,
      extension_cords_used: null,
      outlet_condition: 'good',
      notes: '',
    },
    pit: {
      pit_clean: null,
      pit_debris_present: null,
      pit_lid_present: null,
      pit_lid_sealed: null,
      notes: '',
    },
    pump: {
      pump_age_years: '',
      pump_brand: '',
      pump_model: '',
      visible_corrosion: null,
      vibration_or_noise: null,
      notes: '',
    },
    float: {
      type: 'vertical',
      free_movement: null,
      cycle_tested_ok: null,
      notes: '',
    },
    check_valve: {
      present: null,
      orientation_correct: null,
      leaking: null,
      water_hammer_noise: null,
      notes: '',
    },
    discharge: {
      interior_piping_secure: null,
      exterior_terminates_away_from_foundation: null,
      frozen_or_blocked: null,
      notes: '',
    },
    backup: {
      backup_system_present: null,
      battery_age_years: '',
      charger_functioning: null,
      backup_cycle_tested_ok: null,
      notes: '',
    },
    alarms: {
      high_water_alarm_present: null,
      alarm_tested_ok: null,
      notes: '',
    },
    basement: {
      visible_cracks: null,
      efflorescence_present: null,
      damp_spots_present: null,
      dehumidifier_present: null,
      notes: '',
    },
    summary: {
      overall_condition: 'good',
      risk_level: 'low',
      technician_notes: '',
    },
    recommendations: defaultRecommendations,
    approval: {
      customer_name: '',
      customer_email: '',
      approved_total: '',
      signature_data_url: '',
      signed_at: '',
    },
    total_recommended: 0,
  });
  const [saving, setSaving] = useState(false);
  const [inspectionId, setInspectionId] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [webReportUrl, setWebReportUrl] = useState('');
  const [view, setView] = useState('landing');
  const [archiveItems, setArchiveItems] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const saveInspection = async () => {
    try {
      setSaving(true);
      // Map frontend state to backend model shape (float_section vs float)
      const payload = {
        ...formData,
        float_section: formData.float,
      };
      // Avoid sending duplicate top-level key that backend model does not know about
      delete payload.float;

      if (inspectionId) {
        payload.id = inspectionId;
      }

      const res = await axios.post(`${API}/inspections/sump-pump`, payload);
      const saved = res.data;
      if (!inspectionId && saved?.id) {
        setInspectionId(saved.id);
      }
      return saved.id || inspectionId;
    } catch (err) {
      console.error('Error saving sump pump inspection', err);
      alert('Error saving inspection before generating PDF.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleCreateShareableLink = async () => {
    const id = await saveInspection();
    if (!id) return;
    try {
      // Upload photos if any were selected in the photos step
      if (photoFiles.length > 0) {
        const formDataUpload = new FormData();
        photoFiles.forEach((file) => {
          formDataUpload.append('files', file);
        });
        try {
          await axios.post(
            `${API}/inspections/sump-pump/${id}/photos?step_key=photos`,
            formDataUpload,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        } catch (uploadErr) {
          console.error('Error uploading photos for sump pump inspection', uploadErr);
          alert('Photo upload failed, but the web report link will still be created.');
        }
      }

      const url = `${API}/inspections/sump-pump/${id}/html`;
      setWebReportUrl(url);
      setHasGeneratedReport(true);
      alert('Web report link created. You can now copy it using the button below.');
    } catch (err) {
      console.error('Error creating web report link', err);
      alert('Error creating web report link.');
    }
  };

  const handleEmailReport = async () => {
    try {
      setEmailSending(true);
      setEmailSent(false);
      const id = await saveInspection();
      if (!id) return;
      await axios.post(`${API}/inspections/sump-pump/${id}/send`);
      alert('Report email has been queued for sending.');
      setEmailSent(true);
    } catch (err) {
      console.error('Error emailing sump pump report', err);
      alert('Error emailing report. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };




  // Keep approval info in sync when homeowner or totals change
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      approval: {
        ...prev.approval,
        customer_name: prev.homeowner.homeowner_name || prev.approval.customer_name,
        customer_email: prev.homeowner.email || prev.approval.customer_email,
        approved_total:
          typeof prev.total_recommended === 'number'
            ? prev.total_recommended.toFixed(2)
            : prev.approval.approved_total,
      },
    }));
  }, [formData.homeowner.homeowner_name, formData.homeowner.email, formData.total_recommended]);

  const [errors, setErrors] = useState({
    homeowner: {},
    arrival: {},
  });

  const handleToggleStep = (index) => {
    const key = steps[index].key;
    setActiveStepIndex(index);
    setOpenSteps((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const validateHomeowner = () => {
    const stepErrors = {};
    const data = formData.homeowner;

    if (!data.homeowner_name.trim()) stepErrors.homeowner_name = 'Homeowner name is required.';
    if (!data.address.trim()) stepErrors.address = 'Address is required.';
    if (!data.city.trim()) stepErrors.city = 'City is required.';
    if (!data.inspection_date) stepErrors.inspection_date = 'Inspection date is required.';

    setErrors((prev) => ({
      ...prev,
      homeowner: stepErrors,
    }));

    return Object.keys(stepErrors).length === 0;
  };

  const validateArrival = () => {
    const stepErrors = {};
    const data = formData.arrival;

    if (data.has_power === null) stepErrors.has_power = 'Required.';
    if (data.pump_running_constantly === null) stepErrors.pump_running_constantly = 'Required.';
    if (data.water_in_pit === null) stepErrors.water_in_pit = 'Required.';
    if (data.audible_alarm === null) stepErrors.audible_alarm = 'Required.';
    if (data.visible_alarm === null) stepErrors.visible_alarm = 'Required.';

    setErrors((prev) => ({
      ...prev,
      arrival: stepErrors,
    }));

    return Object.keys(stepErrors).length === 0;
  };

  const resetToNewInspection = () => {
    setFormData({
      homeowner: {
        homeowner_name: '',
        address: '',
        city: '',
        province: '',
        postal_code: '',
        phone: '',
        email: '',
        inspection_date: new Date().toISOString().split('T')[0],
        technician_name: currentUser?.full_name || '',
      },
      arrival: {
        has_power: null,
        pump_running_constantly: null,
        water_in_pit: null,
        audible_alarm: null,
        visible_alarm: null,
        notes: '',
        photos: [],
      },
      electrical: {
        dedicated_circuit: null,
        gfci_protected: null,
        extension_cords_used: null,
        outlet_condition: 'good',
        notes: '',
      },
      pit: {
        pit_clean: null,
        pit_debris_present: null,
        pit_lid_present: null,
        pit_lid_sealed: null,
        notes: '',
      },
      pump: {
        pump_age_years: '',
        pump_brand: '',
        pump_model: '',
        visible_corrosion: null,
        vibration_or_noise: null,
        notes: '',
      },
      float: {
        type: 'vertical',
        free_movement: null,
        cycle_tested_ok: null,
        notes: '',
      },
      check_valve: {
        present: null,
        orientation_correct: null,
        leaking: null,
        water_hammer_noise: null,
        notes: '',
      },
      discharge: {
        interior_piping_secure: null,
        exterior_terminates_away_from_foundation: null,
        frozen_or_blocked: null,
        notes: '',
      },
      backup: {
        backup_system_present: null,
        battery_age_years: '',
        charger_functioning: null,
        backup_cycle_tested_ok: null,
        notes: '',
      },
      alarms: {
        high_water_alarm_present: null,
        alarm_tested_ok: null,
        notes: '',
      },
      basement: {
        visible_cracks: null,
        efflorescence_present: null,
        damp_spots_present: null,
        dehumidifier_present: null,
        notes: '',
      },
      summary: {
        overall_condition: 'good',
        risk_level: 'low',
        technician_notes: '',
      },
      recommendations: defaultRecommendations,
      approval: {
        customer_name: '',
        customer_email: '',
        approved_total: '',
        signature_data_url: '',
        signed_at: '',
      },
      total_recommended: 0,
    });
    setActiveStepIndex(0);
    setOpenSteps([steps[0].key]);
    setCompletedSteps([]);
    setHasGeneratedReport(false);
    setHasSentReport(false);
    setInspectionId(null);
    setPhotoFiles([]);
    setWebReportUrl('');
  };

  const loadInspectionFromArchive = (inspection) => {
    if (!inspection) return;
    setFormData({
      homeowner: inspection.homeowner || formData.homeowner,
      arrival: inspection.arrival || formData.arrival,
      electrical: inspection.electrical || formData.electrical,
      pit: inspection.pit || formData.pit,
      pump: inspection.pump || formData.pump,
      float: inspection.float_section || formData.float,
      check_valve: inspection.check_valve || formData.check_valve,
      discharge: inspection.discharge || formData.discharge,
      backup: inspection.backup || formData.backup,
      alarms: inspection.alarms || formData.alarms,
      basement: inspection.basement || formData.basement,
      summary: inspection.summary || formData.summary,
      recommendations: inspection.recommendations && inspection.recommendations.length > 0
        ? inspection.recommendations
        : defaultRecommendations,
      approval: inspection.approval || formData.approval,
      total_recommended: typeof inspection.total_recommended === 'number'
        ? inspection.total_recommended
        : 0,
    });
    setInspectionId(inspection.id);
    setActiveStepIndex(0);
    setOpenSteps([steps[0].key]);
    setCompletedSteps([]);
    setHasGeneratedReport(false);
    setHasSentReport(false);
    setView('wizard');
  };

  const fetchArchive = async () => {
    setArchiveLoading(true);
    setArchiveError('');
    try {
      const res = await axios.get(`${API}/inspections/sump-pump`);
      setArchiveItems(res.data || []);
    } catch (err) {
      console.error('Error loading sump pump archive', err);
      setArchiveError('Error loading archive. Please try again.');
    } finally {
      setArchiveLoading(false);
    }
  };

  const toggleStepComplete = async (stepKey) => {
    setCompletedSteps((prevCompleted) => {
      const willBeCompleted = !prevCompleted.includes(stepKey);
      const nextCompleted = willBeCompleted
        ? [...prevCompleted, stepKey]
        : prevCompleted.filter((k) => k !== stepKey);

      // When a step is newly marked complete, auto-save the inspection
      if (willBeCompleted) {
        // Save current inspection state
        saveInspection().then(async (id) => {
          if (!id) return;
          // If this is the Photos step, also upload selected photos
          if (stepKey === 'photos' && photoFiles.length > 0) {
            const formDataUpload = new FormData();
            photoFiles.forEach((file) => {
              formDataUpload.append('files', file);
            });
            try {
              await axios.post(
                `${API}/inspections/sump-pump/${id}/photos?step_key=photos`,
                formDataUpload,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                },
              );
            } catch (uploadErr) {
              console.error('Error uploading photos for sump pump inspection', uploadErr);
            }
          }
        });

        // Auto-collapse the step after marking complete
        setOpenSteps((prevOpen) => prevOpen.filter((k) => k !== stepKey));
      }

      return nextCompleted;
    });
  };

  const updateHomeownerField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      homeowner: {
        ...prev.homeowner,
        [field]: value,
      },
    }));
  };

  const updateArrivalField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      arrival: {
        ...prev.arrival,
        [field]: value,
      },
    }));
  };

  const handleArrivalPhotoChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const newPhotos = files.slice(0, 3).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setFormData((prev) => ({
      ...prev,
      arrival: {
        ...prev.arrival,
        photos: newPhotos,
      },
    }));
  };

  const isWinnipeg = formData.homeowner.city.trim().toLowerCase() === 'winnipeg';

  // Step-specific content
  const renderStepContent = (step) => {
    switch (step.key) {
      case 'homeowner':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Capture homeowner contact information and basic job details. City selection controls Winnipeg
              route-week messaging.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Homeowner Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.homeowner.homeowner_name}
                  onChange={(e) => updateHomeownerField('homeowner_name', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="John or Jane Homeowner"
                />
                {errors.homeowner.homeowner_name && (
                  <p className="text-xs text-red-600 mt-1">{errors.homeowner.homeowner_name}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Technician</label>
                <input
                  type="text"
                  value={formData.homeowner.technician_name}
                  onChange={(e) => updateHomeownerField('technician_name', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Inspector name"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Service Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.homeowner.address}
                  onChange={(e) => updateHomeownerField('address', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Street address"
                />
                {errors.homeowner.address && (
                  <p className="text-xs text-red-600 mt-1">{errors.homeowner.address}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.homeowner.city}
                  onChange={(e) => updateHomeownerField('city', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="e.g., Thunder Bay or Winnipeg"
                />
                {errors.homeowner.city && (
                  <p className="text-xs text-red-600 mt-1">{errors.homeowner.city}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Province</label>
                <input
                  type="text"
                  value={formData.homeowner.province}
                  onChange={(e) => updateHomeownerField('province', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="ON, MB, etc."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Postal Code</label>
                <input
                  type="text"
                  value={formData.homeowner.postal_code}
                  onChange={(e) => updateHomeownerField('postal_code', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="A1A 1A1"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.homeowner.phone}
                  onChange={(e) => updateHomeownerField('phone', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="(555) 555-5555"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.homeowner.email}
                  onChange={(e) => updateHomeownerField('email', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="homeowner@example.com"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Inspection Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.homeowner.inspection_date}
                  onChange={(e) => updateHomeownerField('inspection_date', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                {errors.homeowner.inspection_date && (
                  <p className="text-xs text-red-600 mt-1">{errors.homeowner.inspection_date}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'arrival':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Document the initial condition on arrival, including power status, pump behavior, water levels
              and any active alarms. Attach a quick photo of the pit/area.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{
                key: 'has_power',
                label: 'Power to sump pump circuit?',
              }, {
                key: 'pump_running_constantly',
                label: 'Pump running constantly on arrival?',
              }, {
                key: 'water_in_pit',
                label: 'Standing water present in pit?',
              }, {
                key: 'audible_alarm',
                label: 'Any audible alarm sounding?',
              }, {
                key: 'visible_alarm',
                label: 'Any visible alarm light active?',
              }].map((q) => (
                <div key={q.key} className="border rounded-lg bg-white p-3">
                  <p className="text-xs font-semibold text-gray-800 mb-2">{q.label}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => updateArrivalField(q.key, true)}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.arrival[q.key] === true
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => updateArrivalField(q.key, false)}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.arrival[q.key] === false
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                  {errors.arrival[q.key] && (
                    <p className="text-xs text-red-600 mt-1">{errors.arrival[q.key]}</p>
                  )}
                </div>
              ))}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Arrival Notes</label>
              <textarea
                rows={3}
                value={formData.arrival.notes}
                onChange={(e) => updateArrivalField('notes', e.target.value)}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Brief notes on what you observed on arrival."
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Arrival Photo (up to 3 images)
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleArrivalPhotoChange}
                className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 border border-gray-300 rounded-lg p-1"
              />
              {formData.arrival.photos?.length > 0 && (
                <div className="flex mt-2 gap-2 overflow-x-auto">
                  {formData.arrival.photos.map((p) => (
                    <div key={p.id} className="w-16 h-16 rounded-md overflow-hidden border border-gray-200 flex-shrink-0">
                      <img
                        src={p.previewUrl}
                        alt="Arrival"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-gray-500 mt-1">
                Photos are stored locally in this session for now and will be uploaded when backend integration
                is wired in.
              </p>
            </div>
          </div>
        );

      case 'electrical':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Check that the sump pump electrical supply is safe and appropriate. Note any unsafe practices like
              permanent extension cords or overloaded receptacles.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Dedicated circuit for sump pump?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      electrical: { ...prev.electrical, dedicated_circuit: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.electrical.dedicated_circuit === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      electrical: { ...prev.electrical, dedicated_circuit: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.electrical.dedicated_circuit === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">GFCI protected receptacle?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      electrical: { ...prev.electrical, gfci_protected: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.electrical.gfci_protected === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      electrical: { ...prev.electrical, gfci_protected: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.electrical.gfci_protected === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Extension cords in permanent use?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      electrical: { ...prev.electrical, extension_cords_used: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.electrical.extension_cords_used === true
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      electrical: { ...prev.electrical, extension_cords_used: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.electrical.extension_cords_used === false
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Electrical notes</label>
              <textarea
                rows={3}
                value={formData.electrical.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  electrical: { ...prev.electrical, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional electrical concerns."
              />
            </div>
          </div>
        );

      case 'pit':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Inspect the sump pit for cleanliness, debris, and presence of a secure lid.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Pit clean and free of debris?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      pit: { ...prev.pit, pit_clean: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.pit.pit_clean === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      pit: { ...prev.pit, pit_clean: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.pit.pit_clean === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Pit lid present and secure?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      pit: { ...prev.pit, pit_lid_present: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.pit.pit_lid_present === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      pit: { ...prev.pit, pit_lid_present: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.pit.pit_lid_present === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Pit notes</label>
              <textarea
                rows={3}
                value={formData.pit.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  pit: { ...prev.pit, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional pit observations."
              />
            </div>
          </div>
        );

      case 'pump':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Assess the primary pump for age, condition, and any obvious defects.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Approximate pump age (years)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.pump.pump_age_years}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    pump: { ...prev.pump, pump_age_years: e.target.value },
                  }))}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="e.g., 5"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Pump brand / model</label>
                <input
                  type="text"
                  value={formData.pump.pump_brand}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    pump: { ...prev.pump, pump_brand: e.target.value },
                  }))}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Brand"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Visible corrosion or damage?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      pump: { ...prev.pump, visible_corrosion: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.pump.visible_corrosion === true
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      pump: { ...prev.pump, visible_corrosion: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.pump.visible_corrosion === false
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Pump notes</label>
              <textarea
                rows={3}
                value={formData.pump.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  pump: { ...prev.pump, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional pump observations."
              />
            </div>
          </div>
        );

      case 'float':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Verify that the float switch moves freely and reliably turns the pump on/off.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Float type</label>
                <select
                  value={formData.float.type}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    float: { ...prev.float, type: e.target.value },
                  }))}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                >
                  <option value="vertical">Vertical float</option>
                  <option value="tethered">Tethered float</option>
                  <option value="integrated">Integrated (internal) switch</option>
                </select>
              </div>
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Float moves freely?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      float: { ...prev.float, free_movement: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.float.free_movement === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      float: { ...prev.float, free_movement: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.float.free_movement === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Float notes</label>
              <textarea
                rows={3}
                value={formData.float.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  float: { ...prev.float, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional float observations."
              />
            </div>
          </div>
        );

      case 'check_valve':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Inspect the check valve for leaks, orientation, and noise that may indicate water hammer.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{
                key: 'present',
                label: 'Check valve present?',
              }, {
                key: 'orientation_correct',
                label: 'Check valve installed correctly?',
              }, {
                key: 'leaking',
                label: 'Any leaks at check valve?',
              }, {
                key: 'water_hammer_noise',
                label: 'Water hammer noise present?',
              }].map((q) => (
                <div key={q.key} className="border rounded-lg bg-white p-3">
                  <p className="text-xs font-semibold text-gray-800 mb-2">{q.label}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        check_valve: { ...prev.check_valve, [q.key]: true },
                      }))}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.check_valve[q.key] === true
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        check_valve: { ...prev.check_valve, [q.key]: false },
                      }))}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.check_valve[q.key] === false
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Check valve notes</label>
              <textarea
                rows={3}
                value={formData.check_valve.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  check_valve: { ...prev.check_valve, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional check valve observations."
              />
            </div>
          </div>
        );

      case 'discharge':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Follow the discharge line from pit to exterior and note any issues with termination or freezing risk.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{
                key: 'interior_piping_secure',
                label: 'Interior discharge piping secure?',
              }, {
                key: 'exterior_terminates_away_from_foundation',
                label: 'Discharge terminates away from foundation?',
              }, {
                key: 'frozen_or_blocked',
                label: 'Any signs of freezing or blockage?',
              }].map((q) => (
                <div key={q.key} className="border rounded-lg bg-white p-3">
                  <p className="text-xs font-semibold text-gray-800 mb-2">{q.label}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        discharge: { ...prev.discharge, [q.key]: true },
                      }))}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.discharge[q.key] === true
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        discharge: { ...prev.discharge, [q.key]: false },
                      }))}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.discharge[q.key] === false
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Discharge notes</label>
              <textarea
                rows={3}
                value={formData.discharge.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  discharge: { ...prev.discharge, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional discharge observations."
              />
            </div>
          </div>
        );

      case 'backup':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              If a backup system is present, confirm it is functional and the battery/charger are in good shape.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Backup system present?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      backup: { ...prev.backup, backup_system_present: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.backup.backup_system_present === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      backup: { ...prev.backup, backup_system_present: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.backup.backup_system_present === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Battery age (years)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.backup.battery_age_years}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    backup: { ...prev.backup, battery_age_years: e.target.value },
                  }))}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="e.g., 2"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Charger functioning?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      backup: { ...prev.backup, charger_functioning: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.backup.charger_functioning === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      backup: { ...prev.backup, charger_functioning: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.backup.charger_functioning === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">Backup cycle tested OK?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      backup: { ...prev.backup, backup_cycle_tested_ok: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.backup.backup_cycle_tested_ok === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      backup: { ...prev.backup, backup_cycle_tested_ok: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.backup.backup_cycle_tested_ok === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Backup notes</label>
              <textarea
                rows={3}
                value={formData.backup.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  backup: { ...prev.backup, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional backup system observations."
              />
            </div>
          </div>
        );

      case 'alarms':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Confirm any alarm systems are present and functioning.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">High-water alarm present?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      alarms: { ...prev.alarms, high_water_alarm_present: true },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.alarms.high_water_alarm_present === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      alarms: { ...prev.alarms, high_water_alarm_present: false },
                    }))}
                    className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                      formData.alarms.high_water_alarm_present === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Alarm notes</label>
              <textarea
                rows={3}
                value={formData.alarms.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  alarms: { ...prev.alarms, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional alarm observations."
              />
            </div>
          </div>
        );

      case 'photos':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Capture up to five overall inspection photos (before/after shots, pit, discharge, etc.). These will
              be referenced in the PDF summary.
            </p>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Inspection photos (max 5 images)
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []).slice(0, 5);
                  setPhotoFiles(files);
                }}
                className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 border border-gray-300 rounded-lg p-1"
              />
              {photoFiles.length > 0 && (
                <p className="text-[11px] text-gray-500 mt-1">
                  {photoFiles.length} file{photoFiles.length !== 1 ? 's' : ''} selected. They will be uploaded when
                  you generate the PDF.
                </p>
              )}
            </div>
          </div>
        );

      case 'basement':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Do a quick moisture and foundation scan of the basement.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{
                key: 'visible_cracks',
                label: 'Visible wall/floor cracks?',
              }, {
                key: 'efflorescence_present',
                label: 'Efflorescence present?',
              }, {
                key: 'damp_spots_present',
                label: 'Damp spots present?',
              }, {
                key: 'dehumidifier_present',
                label: 'Dehumidifier present?',
              }].map((q) => (
                <div key={q.key} className="border rounded-lg bg-white p-3">
                  <p className="text-xs font-semibold text-gray-800 mb-2">{q.label}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        basement: { ...prev.basement, [q.key]: true },
                      }))}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.basement[q.key] === true
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        basement: { ...prev.basement, [q.key]: false },
                      }))}
                      className={`flex-1 px-2 py-1 rounded text-xs font-semibold border ${
                        formData.basement[q.key] === false
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Basement notes</label>
              <textarea
                rows={3}
                value={formData.basement.notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  basement: { ...prev.basement, notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Any additional basement observations."
              />
            </div>
          </div>
        );

      case 'summary':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Provide a simple overall classification of the sump pump system.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Overall condition</label>
                <select
                  value={formData.summary.overall_condition}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    summary: { ...prev.summary, overall_condition: e.target.value },
                  }))}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                >
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Risk level</label>
                <select
                  value={formData.summary.risk_level}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    summary: { ...prev.summary, risk_level: e.target.value },
                  }))}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Technician summary notes</label>
              <textarea
                rows={4}
                value={formData.summary.technician_notes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  summary: { ...prev.summary, technician_notes: e.target.value },
                }))}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                placeholder="Brief plain-language explanation of system condition and concerns."
              />
            </div>
          </div>
        );

      case 'recommendations':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select recommended upgrades and enter pricing for each. The total will be shown at the bottom.
            </p>
            <div className="space-y-3">
              {formData.recommendations.map((item, idx) => (
                <div key={item.code} className="flex items-start gap-3 bg-white border rounded-lg p-3">
                  <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={(e) => {
                      const updated = [...formData.recommendations];
                      updated[idx] = { ...item, selected: e.target.checked };
                      const total = updated
                        .filter(r => r.selected && typeof r.price === 'number')
                        .reduce((sum, r) => sum + (r.price || 0), 0);
                      setFormData(prev => ({ ...prev, recommendations: updated, total_recommended: total }));
                    }}
                    className="mt-1 w-4 h-4 text-blue-600 rounded"
                  />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-800">
                        {item.label}
                        {item.guideline && (
                          <span className="ml-1 text-[10px] font-normal text-gray-500">
                            ({item.guideline} guideline)
                          </span>
                        )}
                      </p>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price ?? ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : parseFloat(e.target.value);
                          const updated = [...formData.recommendations];
                          updated[idx] = { ...item, price: value };
                          const total = updated
                            .filter(r => r.selected && typeof r.price === 'number')
                            .reduce((sum, r) => sum + (r.price || 0), 0);
                          setFormData(prev => ({
                            ...prev,
                            recommendations: updated,
                            total_recommended: total,
                          }));
                        }}
                        className="w-28 p-2 rounded border border-gray-300 text-xs text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Price"
                      />
                    </div>
                    {item.code === 'other' && (
                      <textarea
                        rows={2}
                        value={item.notes ?? ''}
                        onChange={(e) => {
                          const updated = [...formData.recommendations];
                          updated[idx] = { ...item, notes: e.target.value };
                          setFormData(prev => ({ ...prev, recommendations: updated }));
                        }}
                        className="mt-1 w-full p-2 rounded border border-gray-300 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Describe the other recommended work"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-2 border-t border-gray-200 flex justify-between items-center">
              <p className="text-xs text-gray-600">Total recommended upgrades:</p>
              <p className="text-sm font-bold text-gray-900">
                {typeof formData.total_recommended === 'number'
                  ? `$${formData.total_recommended.toFixed(2)}`
                  : '$0.00'}
              </p>
            </div>
          </div>
        );

      case 'pdf':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This step will handle final paperwork: creating a shareable web report link that you can send to the
              homeowner and your office/admin.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={handleCreateShareableLink}
                className={`px-4 py-2 rounded-md text-xs font-semibold border ${
                  hasGeneratedReport
                    ? 'bg-white text-green-700 border-green-600 hover:bg-green-50'
                    : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                }`}
              >
                {saving ? 'Saving…' : hasGeneratedReport ? 'Link Created' : 'Create Web Report Link'}
              </button>
              <button
                type="button"
                disabled={emailSending}
                onClick={handleEmailReport}
                className={`px-4 py-2 rounded-md text-xs font-semibold border ${
                  emailSent
                    ? 'bg-white text-green-700 border-green-600 hover:bg-green-50'
                    : 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600'
                }`}
              >
                {emailSending ? 'Emailing…' : emailSent ? 'Email Sent' : 'Email Report'}
              </button>
            </div>

            {hasGeneratedReport && webReportUrl && (
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <input
                  type="text"
                  readOnly
                  value={webReportUrl}
                  className="flex-1 px-3 py-2 rounded-md border border-gray-300 text-xs text-gray-700 bg-gray-50"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!webReportUrl) return;
                    window.open(webReportUrl, '_blank', 'noopener,noreferrer');
                  }}
                  className="px-4 py-2 rounded-md text-xs font-semibold border bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                >
                  View Report
                </button>
              </div>
            )}

            <p className="text-[11px] text-gray-500">
              Click "Create Web Report Link" to generate a shareable report URL. You can then copy it from the box
              above and share it with the customer and office.
            </p>
          </div>
        );

      default:
        return (
          <p className="text-sm text-gray-500">
            Detailed fields for &quot;{step.title}&quot; are coming soon.
          </p>
        );
    }
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Production
            </button>
          </div>
          <div className="text-center mb-10">
            <div className="text-5xl mb-4">💧</div>
            <h1 className="text-3xl font-bold mb-2">Sump Pump Inspections</h1>
            <p className="text-gray-600 text-sm">
              Start a new inspection or open a previous one from the archive.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <button
              type="button"
              onClick={() => {
                resetToNewInspection();
                setView('wizard');
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-xl shadow-lg flex flex-col items-center justify-center gap-2"
            >
              <span className="text-3xl">➕</span>
              <span className="text-base">Start New Inspection</span>
            </button>
            <button
              type="button"
              onClick={() => {
                fetchArchive();
                setView('archive');
              }}
              className="w-full bg-white hover:bg-gray-50 text-gray-800 font-semibold py-4 rounded-xl shadow-lg border border-gray-200 flex flex-col items-center justify-center gap-2"
            >
              <span className="text-3xl">📂</span>
              <span className="text-base">View Archive</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'archive') {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setView('landing')}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold">Sump Pump Inspection Archive</h1>
            <button
              type="button"
              onClick={fetchArchive}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Refresh
            </button>
          </div>

          {archiveLoading && (
            <div className="text-center py-10 text-gray-500 text-sm">Loading inspections…</div>
          )}

          {archiveError && !archiveLoading && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {archiveError}
            </div>
          )}

          {!archiveLoading && archiveItems.length === 0 && !archiveError && (
            <div className="text-center py-12 text-gray-500 text-sm">
              No inspections found yet.
            </div>
          )}

          {!archiveLoading && archiveItems.length > 0 && (
            <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Date</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Homeowner</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">City</th>
                  </tr>
                </thead>
                <tbody>
                  {archiveItems.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-blue-50 cursor-pointer"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/inspections/sump-pump/${item.id}`);
                          loadInspectionFromArchive(res.data);
                        } catch (err) {
                          console.error('Error loading inspection', err);
                          alert('Error loading inspection. Please try again.');
                        }
                      }}
                    >
                      <td className="px-4 py-2 text-gray-800">
                        {item.homeowner?.inspection_date || item.created_date?.slice(0, 10) || 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-gray-800">
                        {item.homeowner?.homeowner_name || 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-gray-800">
                        {item.homeowner?.city || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }


  // View routing for Sump Pump module
  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Production
            </button>
          </div>
          <div className="text-center mb-10">
            <div className="text-5xl mb-4">💧</div>
            <h1 className="text-3xl font-bold mb-2">Sump Pump Inspections</h1>
            <p className="text-gray-600 text-sm">
              Start a new inspection or open a previous one from the archive.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <button
              type="button"
              onClick={() => {
                resetToNewInspection();
                setView('wizard');
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-xl shadow-lg flex flex-col items-center justify-center gap-2"
            >
              <span className="text-3xl">➕</span>
              <span className="text-base">Start New Inspection</span>
            </button>
            <button
              type="button"
              onClick={() => {
                fetchArchive();
                setView('archive');
              }}
              className="w-full bg-white hover:bg-gray-50 text-gray-800 font-semibold py-4 rounded-xl shadow-lg border border-gray-200 flex flex-col items-center justify-center gap-2"
            >
              <span className="text-3xl">📂</span>
              <span className="text-base">View Archive</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'archive') {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setView('landing')}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold">Sump Pump Inspection Archive</h1>
            <button
              type="button"
              onClick={fetchArchive}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Refresh
            </button>
          </div>

          {archiveLoading && (
            <div className="text-center py-10 text-gray-500 text-sm">Loading inspections…</div>
          )}

          {archiveError && !archiveLoading && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {archiveError}
            </div>
          )}

          {!archiveLoading && archiveItems.length === 0 && !archiveError && (
            <div className="text-center py-12 text-gray-500 text-sm">
              No inspections found yet.
            </div>
          )}

          {!archiveLoading && archiveItems.length > 0 && (
            <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Date</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Homeowner</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">City</th>
                  </tr>
                </thead>
                <tbody>
                  {archiveItems.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-blue-50 cursor-pointer"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/inspections/sump-pump/${item.id}`);
                          loadInspectionFromArchive(res.data);
                        } catch (err) {
                          console.error('Error loading inspection', err);
                          alert('Error loading inspection. Please try again.');
                        }
                      }}
                    >
                      <td className="px-4 py-2 text-gray-800">
                        {item.homeowner?.inspection_date || item.created_date?.slice(0, 10) || 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-gray-800">
                        {item.homeowner?.homeowner_name || 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-gray-800">
                        {item.homeowner?.city || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }




  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setView('landing')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back
          </button>
          <div className="text-right text-xs text-gray-500">
            Step {activeStepIndex + 1} of {steps.length}
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="text-3xl">💧</div>
          <div>
            <h1 className="text-2xl font-bold">Sump Pump Inspection</h1>
            <p className="text-xs text-gray-500">Accordion-style steps with easy navigation to previous sections.</p>
          </div>
        </div>

        <div className="space-y-3">
          {steps.map((step, index) => {
            const isActive = index === activeStepIndex;
            const isOpen = openSteps.includes(step.key);
            const isCompleted = completedSteps.includes(step.key);

            return (
              <div
                key={step.key}
                className={`border rounded-lg shadow-sm ${isCompleted ? 'border-green-500 bg-green-50' : 'bg-white'}`}
              >
                <button
                  type="button"
                  onClick={() => handleToggleStep(index)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-gray-500">STEP {step.id}</span>
                    <span className="font-semibold text-gray-800">{step.title}</span>
                    {isCompleted && (
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold">
                        Completed
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400">{isOpen ? '▼' : '▶'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t px-4 py-3 space-y-4 bg-gray-50">
                    {renderStepContent(step)}

                    <div className="flex items-center justify-end pt-2 border-t border-gray-200 mt-2">
                      <button
                        type="button"
                        onClick={() => toggleStepComplete(step.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                          isCompleted
                            ? 'bg-white text-green-700 border-green-600 hover:bg-green-50'
                            : 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                        }`}
                      >
                        {isCompleted ? 'Mark as Incomplete' : 'Mark Step Complete'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Vehicle Inspections Component

export default SumpPumpInspectionModule;
